import React, { useState } from "react";
import { ArrowUpRight, Plus, Minus } from "lucide-react";
import { blogPosts, faqs } from "../data/mock";
import { Reveal } from "./ui/Reveal";

// Always shows the 3 most recent blog posts, no manual list to maintain.
// Sorts by `date` when it's set; posts without a date fall back to their
// position in the `blogPosts` array (later in the array = more recent),
// so this works correctly today and keeps working once dates are filled in.
const getLatestBlogs = (posts, count = 3) => {
  return [...posts]
    .map((post, index) => ({ ...post, __order: index }))
    .sort((a, b) => {
      if (a.date && b.date) return new Date(b.date) - new Date(a.date);
      if (a.date) return -1;
      if (b.date) return 1;
      return b.__order - a.__order;
    })
    .slice(0, count);
};

export const Updates = () => {
  const latestBlogs = getLatestBlogs(blogPosts, 3);

  return (
    <section className="py-24 bg-neutral-50">
      <div className="max-w-[1280px] mx-auto px-6">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="inline-block text-[11px] tracking-[0.18em] uppercase text-neutral-500 border border-black/10 rounded-full px-3 py-1.5 bg-white">Knowledge center</span>
              <h2 className="mt-5 text-[32px] sm:text-[44px] leading-[1.05] tracking-[-0.02em] font-semibold text-neutral-950">Insights from the field</h2>
            </div>
            <div className="relative inline-flex group self-start md:self-auto">
              <div className="absolute -inset-1 rounded-xl opacity-50 blur-md bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-500 transition-opacity duration-300 group-hover:opacity-80" />
              <a
                href="/category/blogs"
                className="relative z-10 inline-flex items-center gap-1.5 bg-black text-white pl-5 pr-4 py-2.5 rounded-xl text-sm font-medium transition-transform duration-200 group-hover:-translate-y-0.5"
              >
                View all posts <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {latestBlogs.map((post, i) => (
            <Reveal key={post.slug} delay={i * 0.1}>
              <a
                href={`/blog/${post.slug}`}
                className="group bg-white rounded-3xl p-7 border border-black/5 hover:shadow-lg transition-all block h-full"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{post.category}</span>
                <h3 className="mt-3 text-lg font-semibold text-neutral-950 leading-snug">{post.title}</h3>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-neutral-800 group-hover:gap-2.5 transition-all">
                  Read more <ArrowUpRight className="w-4 h-4" />
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="py-24">
      <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-14">
        <Reveal>
          <span className="inline-block text-[11px] tracking-[0.18em] uppercase text-neutral-500 border border-black/10 rounded-full px-3 py-1.5">FAQ</span>
          <h2 className="mt-5 text-[30px] sm:text-[42px] leading-[1.05] tracking-[-0.02em] font-semibold text-neutral-950">Common questions</h2>
          <p className="mt-4 text-neutral-600">Answers to what most people ask before getting started.</p>
          <div className="mt-10 p-6 rounded-3xl border border-black/10 bg-neutral-50">
            <div className="font-semibold text-neutral-950">Have a different question?</div>
            <div className="text-sm text-neutral-500 mt-1">Reach out directly, no bots involved</div>
            <div className="relative inline-flex group mt-5">
              <div className="absolute -inset-1 rounded-xl opacity-50 blur-md bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-500 transition-opacity duration-300 group-hover:opacity-80" />
              <a
                href="/connect"
                className="relative z-10 inline-flex items-center gap-1.5 bg-black text-white pl-5 pr-4 py-2.5 rounded-xl text-sm font-medium transition-transform duration-200 group-hover:-translate-y-0.5"
              >
                Contact us <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </Reveal>
        <div className="space-y-3">
          {faqs.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <Reveal key={item.q} delay={i * 0.06}>
                <div className="border border-black/10 rounded-2xl bg-white overflow-hidden">
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left"
                  >
                    <span className="font-medium text-neutral-950">{item.q}</span>
                    <span className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                      {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 text-neutral-600 text-sm leading-relaxed">{item.a}</div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
